/**
 * Frontier-risk substrate (HEX-40, lifts ADR-0006 §10; ADR-0012 §5; HEX-42
 * distance-banded encounter rates).
 *
 * Two compounding mechanisms make safe grass less safe and the frontier
 * meaningfully dangerous:
 *
 *  1. **Distance-from-safe-zone scaling** — the ground latent-encounter
 *     rate is multiplied by `safeZoneScale(d)` where `d` is the hex
 *     distance from the nearest safe zone (V1: just origin). Spawn (d=0)
 *     is baseline; the far frontier saturates at `1 + SAFE_ZONE_SCALE_MAX`.
 *     World-gen encounter rates use a step-function band (`safeZoneBand`)
 *     so the spawn-vs-frontier gradient lands at the HEX-42 design numbers.
 *
 *  2. **Ground latent-encounter roll** — every ground cross has a small
 *     one-shot chance to mutate the tile into an `encounter`, with a
 *     rarity-weighted enemy pick (`pickLatentEnemy`). Once mutated, the
 *     tile lives as an encounter for future travellers; the discoverer
 *     faces it immediately on the same cross (see `TileLifecycle.onCross`).
 *
 * HEX-40/HEX-42 scaled the encounter *rate* by distance; HEX-64 adds *tier*
 * scaling — `pickLatentEnemy` now takes the tile's safe-zone distance and
 * reads the shared tier-band table (`tierBands.tierWeightsAt`), so the spawn
 * region stays pure-common and high tiers unlock only on the deep frontier.
 *
 * The spawn-town cluster (5 origin cells) is excluded from the latent
 * roll — `isSpawnClusterCell` — so the spawn-town stays absolutely safe.
 */

import { ENEMY_TEMPLATES } from '../combat/enemies';
import type { EnemyTemplate } from '../combat/enemies';
import type { EnemyTier } from '../combat/types';
import { originDistance } from '../worldgen/distance';
import { pickTier, tierWeightsAt } from './tierBands';

/** Base latent-encounter rate on a ground cross (ADR-0012 §5; HEX-40). */
export const LATENT_ENCOUNTER_BASE_RATE = 0.01;

/**
 * Distance-curve cap for the continuous safe-zone scale.
 * `safeZoneScale` saturates at `1 + SAFE_ZONE_SCALE_MAX` (i.e. 3×) once
 * `d >= SAFE_ZONE_SCALE_DISTANCE`. Linear in between.
 */
export const SAFE_ZONE_SCALE_DISTANCE = 256;
export const SAFE_ZONE_SCALE_MAX = 2;

/**
 * Safe-zone ease radius (hexes) around a liberated town (HEX-122 / ADR-0018 §4).
 * Within this radius the effective safe-zone distance is the minimum of the
 * origin distance and the distance to the nearest liberated town.
 */
export const SETTLEMENT_EASE_RADIUS = 16;

/**
 * Effective safe-zone distance at `(q, r)`. Returns the minimum of the raw
 * origin distance and the distance to any liberated town within
 * `SETTLEMENT_EASE_RADIUS` hexes. Unliberated towns do NOT ease the distance.
 *
 * The `seed` parameter is accepted but currently unused; callers that pass it
 * need no change. The optional `liberatedTowns` list is the Wave-3 activation
 * point (HEX-122): when provided the function applies the town-based easing.
 */
export function safeZoneDistance(
  q: number,
  r: number,
  seed?: number,
  liberatedTowns?: readonly { q: number; r: number }[],
): number {
  void seed;
  let d = originDistance(q, r);
  if (liberatedTowns) {
    for (const town of liberatedTowns) {
      const dq = q - town.q;
      const dr = r - town.r;
      const dt = (Math.abs(dq) + Math.abs(dr) + Math.abs(dq + dr)) / 2;
      if (dt <= SETTLEMENT_EASE_RADIUS) {
        d = Math.min(d, dt);
      }
    }
  }
  return d;
}

/**
 * Continuous difficulty multiplier as a function of safe-zone distance.
 *
 *     f(d) = 1 + SAFE_ZONE_SCALE_MAX × min(d / SAFE_ZONE_SCALE_DISTANCE, 1)
 *
 * `f(0) === 1` (safe spawn baseline), `f(SAFE_ZONE_SCALE_DISTANCE) === 3`
 * (saturated frontier). Used by the latent-encounter roll where a smooth
 * gradient reads better than a step function.
 */
export function safeZoneScale(d: number): number {
  const normalised = Math.min(d / SAFE_ZONE_SCALE_DISTANCE, 1);
  return 1 + SAFE_ZONE_SCALE_MAX * normalised;
}

/**
 * Distance bands used by the world-gen encounter rate (HEX-42 design lock).
 * Band 0 covers the safe-zone neighbourhood; later bands escalate as the
 * player pushes out. World-gen reads these as a step function so the rate
 * curve is felt as discrete jumps in density.
 */
export const SAFE_ZONE_BAND_MAX_DISTANCES: readonly number[] = [5, 50, 150];

/** Returns the band index (0..3) for a given safe-zone distance. */
export function safeZoneBand(d: number): number {
  for (let i = 0; i < SAFE_ZONE_BAND_MAX_DISTANCES.length; i++) {
    const cap = SAFE_ZONE_BAND_MAX_DISTANCES[i];
    if (cap !== undefined && d <= cap) return i;
  }
  return SAFE_ZONE_BAND_MAX_DISTANCES.length;
}

/**
 * Encounter rate per band at world generation (HEX-42). Multiplied by the
 * per-biome encounter affinity in the chunk generator.
 *
 *     Band 0  (d ≤ 5)         — 5%  encounter (spawn neighbourhood)
 *     Band 1  (6 ≤ d ≤ 50)    — 15% encounter (near frontier)
 *     Band 2  (51 ≤ d ≤ 150)  — 30% encounter (mid frontier)
 *     Band 3  (d ≥ 151)       — 45% encounter (far frontier)
 */
export const ENCOUNTER_BAND_RATES: readonly number[] = [0.05, 0.15, 0.3, 0.45];

/** Encounter rate at a given safe-zone distance. */
export function encounterRateAtDistance(d: number): number {
  return ENCOUNTER_BAND_RATES[safeZoneBand(d)] ?? 0;
}

/**
 * Origin spawn-town cluster cells (ADR-0006 HEX-28 amendment; world coords
 * mirror local coords on the origin chunk). Latent-roll skips these so the
 * spawn never mutates.
 */
const SPAWN_CLUSTER: ReadonlyArray<readonly [number, number]> = [
  [0, 0],
  [0, 1],
  [1, 0],
  [1, 1],
  [0, 2],
];

export function isSpawnClusterCell(q: number, r: number): boolean {
  for (const [cq, cr] of SPAWN_CLUSTER) {
    if (cq === q && cr === r) return true;
  }
  return false;
}

/** World-coord list of spawn-cluster cells (exposed for App-level seeding). */
export const SPAWN_CLUSTER_CELLS: ReadonlyArray<readonly [number, number]> = SPAWN_CLUSTER;

function rosterByTier(): Readonly<Record<EnemyTier, readonly EnemyTemplate[]>> {
  const roster: Record<EnemyTier, EnemyTemplate[]> = {
    common: [],
    rare: [],
    epic: [],
    legendary: [],
  };
  for (const enemy of ENEMY_TEMPLATES) {
    roster[enemy.tier].push(enemy);
  }
  return roster;
}

/**
 * Pick a latent-encounter enemy by rarity, tier-scaled to the safe-zone
 * distance `d` of the mutating tile (HEX-64; was a flat tier split in HEX-40).
 *
 * Consumes exactly two values from `random` — one for the tier roll, one for
 * the within-tier pick — so callers can predict PRNG advance for seeded tests.
 * The distance only selects the weight distribution (`tierWeightsAt`); it does
 * not consume an extra random draw.
 *
 * Empty-tier fallback (walk down to the nearest non-empty tier) lives in the
 * shared `pickTier` helper. Throws if no tier has any registered enemy — a
 * generator-time bug, not a runtime path.
 */
export function pickLatentEnemy(random: () => number, d: number): EnemyTemplate {
  const roster = rosterByTier();
  const tier = pickTier(random(), tierWeightsAt(d), (t) => roster[t].length > 0);

  const pool = roster[tier];
  const within = Math.floor(random() * pool.length);
  const enemy = pool[Math.min(within, pool.length - 1)];
  if (enemy === undefined) throw new Error('latent encounter pick missed within-tier');
  return enemy;
}
